import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { helper } from 'src/environments/helper';
import { environment } from 'src/environments/environment';
import { PermissionsService } from 'src/app/services/permissions.service';
import {
    SignSettingsConfigurationService,
    SignerConfiguration,
    Certificate,
    LogoStatusMap,
} from './sign-settings-configuration.service';

@Component({
    selector: 'app-sign-settings-configuration',
    templateUrl: './sign-settings-configuration.component.html',
    styleUrls: ['./sign-settings-configuration.component.scss'],
})
export class SignSettingsConfigurationComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    helper = helper;

    isLoading = false;
    isTestingConnection = false;
    isSyncingCertificates = false;
    isTestingSigning = false;

    // Access control
    isAdmin = false;

    // Service Status
    isHealthy = false;
    isReady = false;
    healthData: any = null;
    readyData: any = null;

    // Configuration
    configurations: SignerConfiguration[] = [];
    configForm!: FormGroup;
    configModified = false;
    initialConfigValues: any = {};

    // Certificates
    certificates: Certificate[] = [];
    selectedCertificateSlug: string = '';
    logoStatus: LogoStatusMap = {};

    // Watermark
    watermarkPreview: string = '';

    constructor(
        private fb: FormBuilder,
        private signerService: SignSettingsConfigurationService,
        public permissionsService: PermissionsService
    ) {
        this.initForm();
        this.checkAdminAccess();
    }

    ngOnInit(): void {
        if (!this.isAdmin) {
            return;
        }
        this.loadData();
    }

    private checkAdminAccess(): void {
        try {
            const userKey = environment.config.userKey;
            const userDataStr = localStorage.getItem(userKey);

            if (userDataStr) {
                let userData = JSON.parse(userDataStr);

                // Dacă rezultatul e tot string, parsează din nou (dublu-serializat)
                if (typeof userData === 'string') {
                    userData = JSON.parse(userData);
                }

                this.isAdmin = userData?.user_type === 'admin';
            } else {
                this.isAdmin = false;
            }
        } catch (e) {
            this.isAdmin = false;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initForm(): void {
        this.configForm = this.fb.group({
            signer_service_url: ['', Validators.required],
            signer_api_key: ['', Validators.required],
            signer_enabled: ['0'],
            signer_default_certificate: [''],
            signer_watermark_template: ['', Validators.required],
            signer_default_position_x: ['50'],
            signer_default_position_y: ['50'],
            signer_default_width: ['200'],
            signer_default_height: ['70'],
            signer_default_page: ['1'],
            signer_default_reason: [''],
            signer_default_location: [''],
            signer_health_webhook_url: [''],
            signer_health_check_enabled: ['0'],
        });

        // Track changes
        this.configForm.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.configModified = this.hasFormChanged();
            });
    }

    private hasFormChanged(): boolean {
        const currentValues = this.configForm.value;
        return JSON.stringify(currentValues) !== JSON.stringify(this.initialConfigValues);
    }

    loadData(): void {
        this.isLoading = true;

        forkJoin({
            health: this.signerService.getHealth(),
            ready: this.signerService.getReady(),
            config: this.signerService.getConfiguration(),
        })
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (results) => {
                    // Health
                    if (results.health?.data?.success) {
                        this.isHealthy = true;
                        this.healthData = results.health.data.data;
                    }

                    // Ready
                    if (results.ready?.data?.success) {
                        this.isReady = true;
                        this.readyData = results.ready.data.data;
                    }

                    // Configuration
                    if (results.config?.data) {
                        this.configurations = results.config.data.configurations || [];
                        this.watermarkPreview = results.config.data.watermark_preview || '';
                        this.populateForm();
                    }

                    // Load certificates if service is healthy
                    if (this.isHealthy && this.isReady) {
                        this.loadCertificates();
                    }
                },
                error: (err) => {
                    console.error('Error loading signer data:', err);
                    Swal.fire({
                        title: helper.dialogConfig.headers.errorAtentie,
                        text: 'Nu s-au putut încărca datele serviciului de semnare.',
                        icon: 'error',
                        confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    });
                },
            });
    }

    private populateForm(): void {
        const formData: any = {};
        this.configurations.forEach((config) => {
            if (this.configForm.contains(config.name)) {
                formData[config.name] = config.value;
            }
        });
        this.configForm.patchValue(formData);
        this.initialConfigValues = { ...this.configForm.value };
        this.configModified = false;
    }

    loadCertificates(): void {
        this.signerService
            .syncCertificates()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    // Response structure: { data: { certificates: [...], default: ..., count: ... } }
                    if (response?.data?.certificates) {
                        this.certificates = response.data.certificates;
                        // Set default certificate if available
                        const defaultCert = this.configForm.get('signer_default_certificate')?.value;
                        if (defaultCert) {
                            this.selectedCertificateSlug = defaultCert;
                        }
                        // Load logo status after certificates
                        this.loadLogoStatus();
                    }
                },
                error: (err) => {
                    console.error('Error loading certificates:', err);
                },
            });
    }

    loadLogoStatus(): void {
        this.signerService
            .getLogoStatus()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (response?.data) {
                        this.logoStatus = response.data;
                    }
                },
                error: (err) => {
                    console.error('Error loading logo status:', err);
                },
            });
    }

    syncCertificates(): void {
        this.isSyncingCertificates = true;
        this.signerService
            .syncCertificates()
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.isSyncingCertificates = false))
            )
            .subscribe({
                next: (response) => {
                    // Response structure: { data: { certificates: [...], default: ..., count: ... } }
                    if (response?.data?.certificates) {
                        this.certificates = response.data.certificates;
                        Swal.fire({
                            title: helper.dialogConfig.headers.success,
                            text: 'Certificatele au fost sincronizate cu succes.',
                            icon: 'success',
                            confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        });
                    }
                },
                error: (err) => {
                    Swal.fire({
                        title: helper.dialogConfig.headers.errorAtentie,
                        text: 'Nu s-au putut sincroniza certificatele.',
                        icon: 'error',
                        confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    });
                },
            });
    }

    testConnection(): void {
        this.isTestingConnection = true;
        this.signerService
            .testConnection()
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.isTestingConnection = false))
            )
            .subscribe({
                next: (response) => {
                    const results = response?.data?.results;
                    const success = response?.data?.success;

                    if (success) {
                        this.isHealthy = true;
                        this.isReady = true;
                        Swal.fire({
                            title: helper.dialogConfig.headers.success,
                            text: 'Conexiunea la serviciul de semnare este funcțională!',
                            icon: 'success',
                            confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        });
                        // Reload certificates after successful connection
                        this.loadCertificates();
                    } else {
                        let errorDetails = '';
                        if (!results?.health?.success) {
                            errorDetails += 'Health check eșuat. ';
                        }
                        if (!results?.ready?.success) {
                            errorDetails += 'Ready check eșuat. ';
                        }
                        if (!results?.certificates?.success) {
                            errorDetails += 'Certificatele nu pot fi accesate. ';
                        }

                        Swal.fire({
                            title: helper.dialogConfig.headers.errorAtentie,
                            text: errorDetails || 'Conexiunea a eșuat.',
                            icon: 'error',
                            confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        });
                    }
                },
                error: (err) => {
                    this.isHealthy = false;
                    this.isReady = false;
                    Swal.fire({
                        title: helper.dialogConfig.headers.errorAtentie,
                        text: 'Nu s-a putut testa conexiunea la serviciul de semnare.',
                        icon: 'error',
                        confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    });
                },
            });
    }

    testSigning(): void {
        const slug = this.selectedCertificateSlug || this.configForm.get('signer_default_certificate')?.value;
        if (!slug) {
            Swal.fire({
                title: helper.dialogConfig.headers.errorAtentie,
                text: 'Selectați un certificat pentru testare.',
                icon: 'warning',
                confirmButtonText: helper.dialogConfig.buttonLabels.close,
            });
            return;
        }

        this.isTestingSigning = true;
        this.signerService
            .testSign(slug)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.isTestingSigning = false))
            )
            .subscribe({
                next: (response) => {
                    const data = response?.data;
                    const timing = data?.timing;
                    const details = data?.details;

                    // Refresh rate limits after test signing
                    this.loadCertificates();

                    if (data?.success && details?.request_id) {
                        Swal.fire({
                            title: helper.dialogConfig.headers.success,
                            html: `
                                <p>Testul de semnare a fost realizat cu succes!</p>
                                <p><small>Timp: ${timing?.total_ms || 'N/A'} ms</small></p>
                            `,
                            icon: 'success',
                            showCancelButton: true,
                            confirmButtonText: 'Descarcă PDF semnat',
                            cancelButtonText: helper.dialogConfig.buttonLabels.close,
                            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
                        }).then((result) => {
                            if (result.isConfirmed) {
                                this.downloadTestFile(details.request_id);
                            }
                        });
                    } else {
                        Swal.fire({
                            title: helper.dialogConfig.headers.success,
                            text: 'Testul de semnare a fost realizat cu succes!',
                            icon: 'success',
                            confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        });
                    }
                },
                error: (err) => {
                    const errorMsg = err?.error?.errors?.message?.[0] || 'Testul de semnare a eșuat.';
                    Swal.fire({
                        title: helper.dialogConfig.headers.errorAtentie,
                        text: errorMsg,
                        icon: 'error',
                        confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    });
                },
            });
    }

    downloadTestFile(requestId: string): void {
        this.signerService.downloadSignedFile(requestId).subscribe({
            next: (blob) => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `test-signed-${requestId}.pdf`;
                link.click();
                window.URL.revokeObjectURL(url);
            },
            error: (err) => {
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: 'Nu s-a putut descărca fișierul semnat.',
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                });
            },
        });
    }

    updateConfiguration(): void {
        if (!this.configModified) {
            return;
        }

        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'Configurația va fi actualizată.',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.saveConfiguration();
            }
        });
    }

    private saveConfiguration(): void {
        this.isLoading = true;

        // Build update payload from form values
        const formValues = this.configForm.value;
        const updates: any[] = [];

        Object.keys(formValues).forEach((key) => {
            const config = this.configurations.find((c) => c.name === key);
            if (config) {
                updates.push({
                    id: config.id,
                    name: key,
                    value: formValues[key],
                });
            }
        });

        this.signerService
            .updateConfiguration(updates)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: () => {
                    this.initialConfigValues = { ...this.configForm.value };
                    this.configModified = false;

                    Swal.fire({
                        title: helper.dialogConfig.headers.success,
                        text: 'Configurația a fost actualizată cu succes.',
                        icon: 'success',
                        confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    });

                    // Reload watermark preview
                    this.loadWatermarkPreview();
                },
                error: (err) => {
                    const errorMsg = err?.error?.errors?.message?.[0] || 'Actualizarea configurației a eșuat.';
                    Swal.fire({
                        title: helper.dialogConfig.headers.errorAtentie,
                        text: errorMsg,
                        icon: 'error',
                        confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    });
                },
            });
    }

    loadWatermarkPreview(): void {
        this.signerService
            .getWatermarkPreview()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (response?.data?.preview) {
                        this.watermarkPreview = response.data.preview;
                    }
                },
            });
    }

    onCertificateSelect(slug: string): void {
        this.selectedCertificateSlug = slug;
        this.configForm.patchValue({ signer_default_certificate: slug });
    }

    getReadyComponentStatus(component: string): string {
        if (!this.readyData?.components) {
            return 'unknown';
        }
        const comp = this.readyData.components[component];
        if (typeof comp === 'string') {
            return comp;
        }
        if (typeof comp === 'object' && comp.status) {
            return comp.status;
        }
        return 'unknown';
    }

    getReadyComponentIcon(component: string): string {
        const status = this.getReadyComponentStatus(component);
        return status === 'connected' || status === 'ok' ? 'check_circle' : 'error';
    }

    getReadyComponentClass(component: string): string {
        const status = this.getReadyComponentStatus(component);
        return status === 'connected' || status === 'ok' ? 'status-ok' : 'status-error';
    }

    getDiskStatusDisplay(): string {
        const disk = this.readyData?.components?.disk;
        if (!disk) {
            return 'unknown';
        }
        return disk.status || 'unknown';
    }

    formatDiskSize(mb: number): string {
        if (!mb) return 'N/A';
        if (mb >= 1024) {
            return (mb / 1024).toFixed(2) + ' GB';
        }
        return mb.toFixed(2) + ' MB';
    }

    getDiskUsageClass(): string {
        const usage = this.readyData?.components?.disk?.usage_percent;
        if (!usage) return '';
        if (usage >= 90) return 'bg-danger';
        if (usage >= 75) return 'bg-warning';
        return 'bg-success';
    }

    getRateLimitClass(rateLimit: any): string {
        if (!rateLimit) return '';
        const percentUsed = ((rateLimit.limit - rateLimit.remaining) / rateLimit.limit) * 100;
        if (percentUsed >= 90) return 'rate-critical';
        if (percentUsed >= 75) return 'rate-warning';
        return 'rate-ok';
    }

    formatResetTime(resetAt: string): string {
        if (!resetAt) return '-';
        const resetDate = new Date(resetAt);
        const now = new Date();
        const diffMs = resetDate.getTime() - now.getTime();

        if (diffMs <= 0) {
            return 'Acum';
        }

        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 60) {
            return `${diffMins} min`;
        }

        const hours = resetDate.getHours().toString().padStart(2, '0');
        const mins = resetDate.getMinutes().toString().padStart(2, '0');
        return `${hours}:${mins}`;
    }

    // ==================== LOGO METHODS ====================

    hasLogo(slug: string): boolean {
        return this.logoStatus[slug]?.has_logo || false;
    }

    isLogoEnabled(slug: string): boolean {
        return this.logoStatus[slug]?.enabled || false;
    }

    onLogoUpload(event: Event, slug: string): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || !input.files[0]) {
            return;
        }

        const file = input.files[0];

        // Validate file type
        if (!file.type.includes('jpeg') && !file.type.includes('jpg')) {
            Swal.fire({
                title: helper.dialogConfig.headers.errorAtentie,
                text: 'Doar fișiere JPEG/JPG sunt permise',
                icon: 'error',
                confirmButtonText: helper.dialogConfig.buttonLabels.close,
            });
            input.value = '';
            return;
        }

        // Validate file size (max 100KB)
        if (file.size > 100 * 1024) {
            Swal.fire({
                title: helper.dialogConfig.headers.errorAtentie,
                text: 'Fișierul trebuie să fie maxim 100KB',
                icon: 'error',
                confirmButtonText: helper.dialogConfig.buttonLabels.close,
            });
            input.value = '';
            return;
        }

        this.isLoading = true;
        this.signerService
            .uploadLogo(slug, file)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isLoading = false;
                    input.value = '';
                })
            )
            .subscribe({
                next: () => {
                    this.loadLogoStatus();
                    Swal.fire({
                        title: helper.dialogConfig.headers.success,
                        text: 'Logo-ul a fost încărcat cu succes',
                        icon: 'success',
                        confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    });
                },
                error: (err) => {
                    const errorMsg = err?.error?.errors?.message?.[0] || err?.error?.message || 'Eroare la încărcarea logo-ului';
                    Swal.fire({
                        title: helper.dialogConfig.headers.errorAtentie,
                        text: errorMsg,
                        icon: 'error',
                        confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    });
                },
            });
    }

    previewLogo(slug: string): void {
        this.signerService
            .getLogo(slug)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (blob) => {
                    const url = URL.createObjectURL(blob);
                    // Open in new window
                    const win = window.open('', '_blank');
                    if (win) {
                        win.document.write(`
                            <html>
                                <head>
                                    <title>Logo - ${slug}</title>
                                    <style>
                                        body {
                                            display: flex;
                                            justify-content: center;
                                            align-items: center;
                                            height: 100vh;
                                            margin: 0;
                                            background-color: #f5f5f5;
                                        }
                                        img {
                                            max-width: 90%;
                                            max-height: 90%;
                                            box-shadow: 0 4px 8px rgba(0,0,0,0.2);
                                        }
                                    </style>
                                </head>
                                <body>
                                    <img src="${url}" alt="Logo ${slug}">
                                </body>
                            </html>
                        `);
                    }
                },
                error: (err) => {
                    Swal.fire({
                        title: helper.dialogConfig.headers.errorAtentie,
                        text: 'Nu s-a putut încărca logo-ul',
                        icon: 'error',
                        confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    });
                },
            });
    }

    deleteLogo(slug: string): void {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'Sigur doriți să ștergeți logo-ul pentru acest certificat?',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                this.isLoading = true;
                this.signerService
                    .deleteLogo(slug)
                    .pipe(
                        takeUntil(this.destroy$),
                        finalize(() => (this.isLoading = false))
                    )
                    .subscribe({
                        next: () => {
                            this.loadLogoStatus();
                            Swal.fire({
                                title: helper.dialogConfig.headers.success,
                                text: 'Logo-ul a fost șters cu succes',
                                icon: 'success',
                                confirmButtonText: helper.dialogConfig.buttonLabels.close,
                            });
                        },
                        error: (err) => {
                            Swal.fire({
                                title: helper.dialogConfig.headers.errorAtentie,
                                text: 'Nu s-a putut șterge logo-ul',
                                icon: 'error',
                                confirmButtonText: helper.dialogConfig.buttonLabels.close,
                            });
                        },
                    });
            }
        });
    }

    toggleLogo(slug: string): void {
        const currentEnabled = this.isLogoEnabled(slug);
        this.signerService
            .toggleLogo(slug, !currentEnabled)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.loadLogoStatus();
                },
                error: (err) => {
                    Swal.fire({
                        title: helper.dialogConfig.headers.errorAtentie,
                        text: 'Nu s-a putut actualiza starea logo-ului',
                        icon: 'error',
                        confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    });
                },
            });
    }
}
